const REVIEWS_GOOGLE_MOBILE_QUERY = '(max-width: 768px)';
const REVIEWS_GOOGLE_RESIZE_DELAY = 150;
const REVIEWS_GOOGLE_DOT_VISIBILITY = 0.5;

function isReviewsGoogleMobile() {
  return window.matchMedia(REVIEWS_GOOGLE_MOBILE_QUERY).matches;
}

// Only the layout that is actually rendered gets initialised: the hidden one has
// no measurable height anyway, so clamping and Swiper there would be wasted.
function getVisibleReviewsContainers(root) {
  const selector = isReviewsGoogleMobile()
    ? '.reviews-google-items-container.mobile'
    : '.reviews-google-items-container.desktop';

  return root.querySelectorAll(selector);
}

// The limit comes from the CSS max-height, so it follows the breakpoints.
function getTextMaxHeight(textElement) {
  const maxHeight = parseFloat(getComputedStyle(textElement).maxHeight);

  return Number.isNaN(maxHeight) ? Infinity : maxHeight;
}

// Restores the untruncated text so every run measures the original content.
function resetReviewText(content, moreButton) {
  const fullText = content.dataset.fullText || content.textContent.trim();

  content.dataset.fullText = fullText;
  content.textContent = fullText;

  if (moreButton) moreButton.hidden = true;

  return fullText;
}

function renderTruncatedText(content, words, wordsCount) {
  content.textContent = `${words.slice(0, wordsCount).join(' ')}... `;
}

// Binary search for the largest word count that still fits the allowed height.
function findFittingWordsCount(textElement, content, words, maxHeight) {
  let low = 1;
  let high = words.length;
  let best = 1;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);

    renderTruncatedText(content, words, middle);

    if (textElement.scrollHeight <= maxHeight) {
      best = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }

  return best;
}

// `sharedHeight` overrides the CSS limit: on mobile every card is clamped to the
// height of the tallest card the visitor has expanded so far.
function clampReviewText(textElement, sharedHeight) {
  const content = textElement.querySelector('.reviews-google-item-text-content');

  if (!content) return;

  // an individually expanded card (desktop) keeps showing its whole text
  if (textElement.dataset.expanded === 'true') return;

  const moreButton = textElement.querySelector('.reviews-google-item-more-link');

  textElement.style.maxHeight = sharedHeight ? `${sharedHeight}px` : '';

  const fullText = resetReviewText(content, moreButton);
  const maxHeight = getTextMaxHeight(textElement);

  if (textElement.scrollHeight <= maxHeight) return;

  if (moreButton) moreButton.hidden = false;

  const words = fullText.split(/\s+/);

  renderTruncatedText(content, words, findFittingWordsCount(textElement, content, words, maxHeight));
}

function getSharedTextHeight(container) {
  const sharedHeight = parseFloat(container.dataset.reviewsSharedTextHeight);

  return Number.isNaN(sharedHeight) ? 0 : sharedHeight;
}

function clampReviewTexts(container) {
  const sharedHeight = getSharedTextHeight(container);

  container.querySelectorAll('.reviews-google-item-text').forEach(function (textElement) {
    clampReviewText(textElement, sharedHeight);
  });
}

// Height of the whole review, measured with the clamp temporarily lifted.
function measureFullTextHeight(textElement) {
  const content = textElement.querySelector('.reviews-google-item-text-content');

  if (!content) return 0;

  const previousMaxHeight = textElement.style.maxHeight;

  resetReviewText(content, null);
  textElement.style.maxHeight = 'none';

  const fullHeight = textElement.scrollHeight;

  textElement.style.maxHeight = previousMaxHeight;

  return fullHeight;
}

// Desktop: only the clicked review opens up, the grid layout stays untouched.
function expandSingleReview(textElement) {
  const content = textElement.querySelector('.reviews-google-item-text-content');
  const moreButton = textElement.querySelector('.reviews-google-item-more-link');

  if (!content) return;

  resetReviewText(content, moreButton);
  textElement.style.maxHeight = 'none';
  textElement.dataset.expanded = 'true';
}

// Mobile: the clicked review sets the height every other card grows to, so the
// cards keep matching. Shorter reviews then fit entirely and lose their button.
function expandReviewsToText(container, textElement) {
  const sharedHeight = Math.max(getSharedTextHeight(container), measureFullTextHeight(textElement));

  container.dataset.reviewsSharedTextHeight = sharedHeight;
  clampReviewTexts(container);

  const slider = container.querySelector('.reviews-google-swiper');

  if (slider && slider.swiper) slider.swiper.update();
}

function initReviewsMoreButtons(container) {
  const isMobile = container.classList.contains('mobile');

  container.querySelectorAll('.reviews-google-item-more-link').forEach(function (button) {
    const textElement = button.closest('.reviews-google-item-text');

    if (!textElement) return;

    button.addEventListener('click', function () {
      if (isMobile) {
        expandReviewsToText(container, textElement);
      } else {
        expandSingleReview(textElement);
      }
    });
  });
}

function expandReviewsGrid(grid, button) {
  function onExpanded(event) {
    if (event.propertyName !== 'max-height') return;

    grid.removeEventListener('transitionend', onExpanded);
    grid.classList.add('expanded');
    grid.style.maxHeight = 'none';
  }

  grid.addEventListener('transitionend', onExpanded);
  grid.style.maxHeight = `${grid.scrollHeight}px`;
  button.classList.add('hidden');
}

function initReviewsLoadMore(container) {
  const grid = container.querySelector('.reviews-google-items-grid');
  const button = container.querySelector('.reviews-google-load-more-btn');

  if (!grid || !button) return;

  if (grid.scrollHeight <= grid.clientHeight + 1) {
    button.classList.add('hidden');
    return;
  }

  button.addEventListener('click', function () {
    expandReviewsGrid(grid, button);
  });
}

// A dot is active while its card is at least half visible inside the viewport.
function setActiveReviewsDots(swiperInstance, dotsBySlide) {
  const containerRect = swiperInstance.el.getBoundingClientRect();
  const visibleLeft = Math.max(containerRect.left, 0);
  const visibleRight = Math.min(containerRect.right, window.innerWidth || containerRect.right);

  swiperInstance.slides.forEach(function (slide, index) {
    const slideDots = dotsBySlide[index];

    if (!slideDots) return;

    // the slide carries the gap as padding, so measure the card inside it
    const card = slide.firstElementChild || slide;
    const slideRect = card.getBoundingClientRect();
    const visibleWidth = Math.min(slideRect.right, visibleRight) - Math.max(slideRect.left, visibleLeft);
    const visibleRatio = slideRect.width > 0 ? Math.max(0, visibleWidth) / slideRect.width : 0;

    slideDots.forEach(function (dot) {
      dot.classList.toggle('active', visibleRatio >= REVIEWS_GOOGLE_DOT_VISIBILITY);
    });
  });
}

function groupReviewsDotsBySlide(dots) {
  const dotsBySlide = {};

  dots.forEach(function (dot) {
    const slideIndex = dot.getAttribute('data-slide-index');

    if (!dotsBySlide[slideIndex]) dotsBySlide[slideIndex] = [];

    dotsBySlide[slideIndex].push(dot);
  });

  return dotsBySlide;
}

function initReviewsSlider(container) {
  const slider = container.querySelector('.reviews-google-swiper');

  if (!slider || slider.swiper || typeof Swiper === 'undefined') return;

  const dots = container.querySelectorAll('.reviews-google-pagination-dot');
  const dotsBySlide = groupReviewsDotsBySlide(dots);

  function syncDots() {
    setActiveReviewsDots(this, dotsBySlide);
  }

  const swiper = new Swiper(slider, {
    slidesPerView: 'auto',
    // the gap between cards comes from the slides' padding-right in CSS, not
    // from Swiper's margin, so the cards' box-shadow is not clipped
    spaceBetween: 0,
    grabCursor: true,
    watchSlidesProgress: true,
    on: {
      init: syncDots,
      slideChange: syncDots,
      setTranslate: syncDots,
      transitionEnd: syncDots,
      resize: syncDots,
    },
  });

  dots.forEach(function (dot) {
    dot.addEventListener('click', function () {
      swiper.slideTo(parseInt(dot.getAttribute('data-slide-index'), 10));
    });
  });
}

function initReviewsGoogle(container) {
  if (container.dataset.reviewsGoogleReady === 'true') return;

  container.dataset.reviewsGoogleReady = 'true';
  clampReviewTexts(container);
  initReviewsMoreButtons(container);

  if (container.classList.contains('mobile')) {
    initReviewsSlider(container);
  } else {
    initReviewsLoadMore(container);
  }
}

function initVisibleReviewsGoogle(root) {
  getVisibleReviewsContainers(root).forEach(initReviewsGoogle);
}

// Both the max-height limit and the card width change with the breakpoints, and
// crossing one also swaps which layout is visible.
function refreshReviewsGoogle() {
  initVisibleReviewsGoogle(document);
  getVisibleReviewsContainers(document).forEach(clampReviewTexts);
}

function runReviewsGoogle() {
  initVisibleReviewsGoogle(document);

  // Web fonts change the text metrics, so re-clamp once they are ready.
  if (document.fonts) {
    document.fonts.ready.then(function () {
      getVisibleReviewsContainers(document).forEach(clampReviewTexts);
    });
  }

  let resizeTimeout = null;

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(refreshReviewsGoogle, REVIEWS_GOOGLE_RESIZE_DELAY);
  });
}

// Swiper is loaded with defer too, so wait for DOMContentLoaded rather than
// running as soon as the document is parsed.
if (document.readyState === 'complete') {
  runReviewsGoogle();
} else {
  document.addEventListener('DOMContentLoaded', runReviewsGoogle);
}

document.addEventListener('shopify:section:load', function (event) {
  initVisibleReviewsGoogle(event.target);
});
